/**
 * Junto Frontend Feature Flag System
 * 
 * This system allows gradual migration from legacy components to Junto frontend
 * components. Studios can enable/disable features progressively to ensure
 * zero visual regression and smooth user experience during the transition.
 */

import React, { createContext, useContext, useState } from 'react';

const FeatureFlagContext = createContext({
  flags: {},
  setFlag: () => {},
  isEnabled: () => false,
  enableAll: () => {},
  disableAll: () => {}
});

// Default feature flags for Sol Room migration
const DEFAULT_FLAGS = {
  // Auth System Migration
  useJuntoAuthPopup: false,
  useJuntoAccountPopup: false,
  
  // Booking System Migration  
  useJuntoBookingPopup: false,
  useJuntoCalendar: false,
  
  // Purchase System Migration
  useJuntoPurchasePopup: false,
  useJuntoCart: false,
  
  // UI Component Migration
  useJuntoButtons: false,
  useJuntoInputs: false,
  useJuntoModals: false,
  
  // Complete Migration (all components)
  useJuntoFrontendCompletely: false
};

export const FeatureFlagProvider = ({ children, initialFlags = {} }) => {
  const [flags, setFlags] = useState({
    ...DEFAULT_FLAGS,
    ...initialFlags
  });

  const setFlag = (flagName, enabled) => {
    setFlags(prev => ({
      ...prev,
      [flagName]: enabled
    }));
    
    // Log for debugging
    console.log(`Feature flag "${flagName}" ${enabled ? 'enabled' : 'disabled'}`);
  };

  const isEnabled = (flagName) => {
    // If complete migration is enabled, all flags are enabled
    if (flags.useJuntoFrontendCompletely) {
      return true;
    }
    
    return Boolean(flags[flagName]);
  };

  const enableAll = () => {
    setFlags(prev => {
      const allEnabled = {};
      Object.keys(prev).forEach(key => {
        allEnabled[key] = true;
      });
      return allEnabled;
    });
    console.log('All Junto frontend features enabled');
  };

  const disableAll = () => {
    setFlags(prev => {
      const allDisabled = {};
      Object.keys(prev).forEach(key => {
        allDisabled[key] = false;
      });
      return allDisabled;
    });
    console.log('All Junto frontend features disabled');
  };

  const value = {
    flags,
    setFlag,
    isEnabled,
    enableAll,
    disableAll
  };

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
};

/**
 * Higher-order component for conditional component rendering
 * based on feature flags
 */
export const withFeatureFlag = (flagName, JuntoComponent, LegacyComponent) => {
  return (props) => {
    const { isEnabled } = useFeatureFlags();
    
    if (isEnabled(flagName)) {
      return <JuntoComponent {...props} />;
    }
    
    return <LegacyComponent {...props} />;
  };
};

/**
 * Hook for conditional component usage
 */
export const useConditionalComponent = (flagName, juntoComponent, legacyComponent) => {
  const { isEnabled } = useFeatureFlags();
  
  return isEnabled(flagName) ? juntoComponent : legacyComponent;
};

/**
 * Development utilities for testing migration
 */
export const FeatureFlagDebugPanel = () => {
  const { flags, setFlag, enableAll, disableAll } = useFeatureFlags();
  
  // Only show in development
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }
  
  const panelStyle = {
    position: 'fixed',
    top: '10px',
    right: '10px',
    background: 'white',
    border: '2px solid #333',
    borderRadius: '8px',
    padding: '16px',
    zIndex: 9999,
    maxHeight: '400px',
    overflowY: 'auto',
    fontSize: '12px',
    fontFamily: 'monospace'
  };
  
  return (
    <div style={panelStyle}>
      <h4 style={{ margin: '0 0 12px 0' }}>Feature Flags</h4>
      
      <div style={{ marginBottom: '12px' }}>
        <button onClick={enableAll} style={{ marginRight: '8px', fontSize: '10px' }}>
          Enable All
        </button>
        <button onClick={disableAll} style={{ fontSize: '10px' }}>
          Disable All
        </button>
      </div>
      
      {Object.entries(flags).map(([flagName, enabled]) => (
        <div key={flagName} style={{ marginBottom: '8px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setFlag(flagName, e.target.checked)}
              style={{ marginRight: '8px' }}
            />
            <span style={{ fontSize: '10px' }}>{flagName}</span>
          </label>
        </div>
      ))}
    </div>
  );
};